import type { ItemId } from "../data/items.ts";
import { Spr } from "../world/spr.ts";
import { hex, type Col } from "./pal.ts";
import { tinSprite } from "./tin.ts";

/** 18×22 item icons for the pack, stall and battle lists. */
const cache = new Map<string, ImageData>();

function bottle(body: readonly Col[], cap: Col, big: boolean): ImageData {
  const s = new Spr(18, 22);
  const w = big ? 12 : 10, x0 = 9 - w / 2, top = big ? 7 : 9;
  for (let y = top; y < 20; y++) {
    for (let x = x0; x < x0 + w; x++) {
      const nx = (x - 9) / (w / 2);
      const ny = (y - top) / (20 - top);
      if (y < top + 2 && Math.abs(nx) > 0.75) continue;
      const lam = -nx * 0.9 + 0.2 - ny * 0.2;
      s.p(x, y, Spr.tone(body, lam, x, y, 0.3));
    }
  }
  s.fill(7, top - 4, 4, 4, hex("#dfe8f4"));
  s.fill(6, top - 6, 6, 2, cap);
  s.vline(x0 + 2, top + 3, 12 - top + 7, hex("#ffffff", 180));
  s.hline(x0 + 1, top + 7, w - 2, hex("#fff6e0", 200));
  s.outline(hex("#1a1430"), 0.35);
  return s.img;
}

/** A folded shirt in an outfit's colors, for the regional cosmetics. */
function shirt(body: Col, trim: Col, accent: Col): ImageData {
  const s = new Spr(18, 22);
  s.fill(3, 7, 12, 11, body);
  s.fill(1, 7, 3, 5, body); s.fill(14, 7, 3, 5, body);
  s.fill(6, 6, 6, 2, trim);
  s.vline(9, 8, 10, trim);
  s.hline(3, 17, 12, accent);
  s.p(7, 11, hex("#ffffff", 150)); s.p(6, 12, hex("#ffffff", 110));
  s.outline(hex("#1a1430"), 0.35);
  return s.img;
}

export function itemIcon(id: ItemId): ImageData {
  const hit = cache.get(id);
  if (hit) return hit;
  let img: ImageData;
  switch (id) {
    case "capsule": img = tinSprite("capsule"); break;
    case "gloss": img = tinSprite("gloss"); break;
    case "myth": img = tinSprite("myth"); break;
    case "sip": img = bottle([hex("#c8ffd6"), hex("#6fe39a"), hex("#2fae6a"), hex("#1d6b4a")], hex("#ff6f91"), false); break;
    case "dunewrap": img = shirt(hex("#e8a050"), hex("#fff0c0"), hex("#8a5a3a")); break;
    case "neonjacket": img = shirt(hex("#2a2350"), hex("#ff4fd8"), hex("#35c6e6")); break;
    case "blossomcoat": img = shirt(hex("#ffd0dc"), hex("#e8537a"), hex("#4fae50")); break;
    case "tallsip": img = bottle([hex("#c8f4ff"), hex("#5fc8f0"), hex("#2a86c8"), hex("#1a4f86")], hex("#ff6f91"), true); break;
    case "fullcope": img = bottle([hex("#fff0c0"), hex("#ffc84a"), hex("#e0892a"), hex("#8e5022")], hex("#58e0cf"), true); break;
    case "cleartea": {
      const s = new Spr(18, 22);
      s.oval(9, 18, 7, 2, hex("#c9d2f0"));
      for (let y = 10; y < 18; y++) for (let x = 3; x < 15; x++) {
        const nx = (x - 9) / 6;
        if (Math.abs(nx) > 1 - (y - 10) * 0.03) continue;
        s.p(x, y, Spr.tone([hex("#ffffff"), hex("#e3e8f4"), hex("#b3bdd6"), hex("#7c86a8")], -nx * 0.9, x, y, 0.3));
      }
      s.oval(9, 10, 6, 1.6, hex("#9be3b0"));
      s.oval(9, 10, 4, 0.8, hex("#d6ffe0"));
      s.line(15, 12, 17, 14, hex("#b3bdd6"));
      s.line(17, 14, 15, 16, hex("#b3bdd6"));
      s.p(8, 6, hex("#ffffff", 160)); s.p(10, 4, hex("#ffffff", 120)); s.p(9, 7, hex("#ffffff", 140));
      s.outline(hex("#1a1430"), 0.35);
      img = s.img;
      break;
    }
    case "sticker": {
      const s = new Spr(18, 22);
      const pts: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
        const r = i % 2 ? 3.4 : 7.5;
        pts.push([9 + Math.cos(a) * r, 12 + Math.sin(a) * r]);
      }
      s.poly(pts, (x, y) => (x + y < 20 ? hex("#fff0a0") : hex("#ffc84a")));
      s.p(9, 12, hex("#ff6f91"));
      s.outline(hex("#ffffff"), 0);
      s.outline(hex("#1a1430"), 0.2);
      img = s.img;
      break;
    }
    case "frame": {
      const s = new Spr(18, 22);
      s.fill(2, 5, 14, 14, hex("#ffc84a"));
      s.fill(4, 7, 10, 10, hex("#5c8fd6"));
      s.fill(4, 13, 10, 4, hex("#4fae50"));
      s.p(11, 9, hex("#fff0a0"));
      s.hline(2, 5, 14, hex("#fff0a0"));
      s.outline(hex("#1a1430"), 0.3);
      img = s.img;
      break;
    }
  }
  cache.set(id, img);
  return img;
}
